import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Choice } from 'src/app/shared/components/select/choice.model';

import { SubbotDescription, SubbotElementData, SubbotParameterType } from '../../../models';
import { AiConnectorService } from '../ai-connector.service';

/**
 * Ai Connector is a subbot with its own box.
 *
 * Form is in a formArray because all fields are driven by backend.
 * We have a nested formArray because type list is a list of text inputs, and the user can add as many inputs as he wants.
 *
 * @remarks Some code here can be used in common with subbot box when it will be migrated in angular.
 */
@Component({
  selector: 'app-ai-connector-dialog',
  templateUrl: './ai-connector-dialog.component.html',
  styleUrls: ['./ai-connector-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AiConnectorDialogComponent implements OnInit, OnDestroy {
  aiConnectorDescription: SubbotDescription;
  subbotParameterType = SubbotParameterType;

  appSelectSimpleChoices: { [paramName: string]: Choice[] } = {};

  form = this.formBuilder.group({
    formArray: this.formBuilder.array([]),
  });

  get formArray() {
    return this.form.get('formArray') as FormArray;
  }

  private subscriptions = new Subscription();

  constructor(
    private aiConnectorService: AiConnectorService,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public elementData: SubbotElementData
  ) {}

  ngOnInit(): void {
    this.aiConnectorService.getAiConnectorDescription().subscribe(({ description }) => {
      this.aiConnectorDescription = description;
      this.initFormFields(this.aiConnectorDescription);
    });

    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => {
        this.elementData.subBotDescription = this.aiConnectorDescription;

        if (!this.elementData.parameters) {
          this.elementData.parameters = {};
        }

        // as there is only the formArray in the form, loop directly over it
        this.formArray.value.forEach((formArrayValue: any) => {
          const paramName = Object.keys(formArrayValue)[0];
          const paramValue = Object.values(formArrayValue)[0];
          const paramType = this.aiConnectorDescription.parameters.find(param => param.name === paramName).type;

          switch (paramType) {
            case 'list': {
              this.elementData.parameters[paramName] = [];

              Object.values(paramValue).forEach(nestedValue => {
                const valueInList = (Object.values(nestedValue)[0] as string).trim();
                this.elementData.parameters[paramName].push(valueInList);
              });
              break;
            }

            // includes string, integer, select...
            default:
              if (typeof paramValue === 'string') {
                this.elementData.parameters[paramName] = paramValue.trim();
              } else {
                this.elementData.parameters[paramName] = paramValue;
              }
          }
        });
      })
    );
  }

  getNestedFormArray(i: number): FormArray {
    const name = this.aiConnectorDescription.parameters[i].name;
    return this.formArray.controls[i].get(name) as FormArray;
  }

  private initFormFields(description: SubbotDescription) {
    description.parameters.forEach(parameter => {
      switch (parameter.type) {
        case SubbotParameterType.list: {
          const formGroup = this.formBuilder.group({
            [parameter.name]: this.formBuilder.array([]),
          });

          let values: string[];
          if (this.elementData.parameters) {
            values = this.elementData.parameters[parameter.name];
          }

          // add a field for each saved value
          if (values?.length) {
            values.forEach(value => {
              const nestedFormGroup = this.newControlInGroup(
                `${parameter.name}`,
                value,
                parameter.type,
                parameter.specification?.regexp
              );

              (formGroup.get(parameter.name) as FormArray).push(nestedFormGroup);
            });
          }
          // one default empty field
          else {
            const nestedFormGroup = this.newControlInGroup(
              `${parameter.name}`,
              parameter.default,
              parameter.type,
              parameter.specification?.regexp
            );
            (formGroup.get(parameter.name) as FormArray).push(nestedFormGroup);
          }

          this.formArray.push(formGroup);
          break;
        }

        case SubbotParameterType.select: {
          this.aiConnectorService.getAiConnectorSelectParams(parameter.name).subscribe(resp => {
            const choices: Choice[] = [];

            resp.forEach(item => {
              choices.push({ id: item.id, label: item.name });
            });

            this.appSelectSimpleChoices[parameter.name] = choices;

            let value: string;
            if (this.elementData.parameters) {
              value = this.elementData.parameters[parameter.name];
            }
            const formGroup = this.newControlInGroup(parameter.name, value ?? parameter.default, parameter.type, null);
            this.formArray.push(formGroup);
          });

          break;
        }
        default: {
          let value: string;
          if (this.elementData.parameters) {
            value = this.elementData.parameters[parameter.name];
          }

          const formGroup = this.newControlInGroup(
            parameter.name,
            value ?? parameter.default,
            parameter.type,
            parameter.specification?.regexp
          );

          this.formArray.push(formGroup);
        }
      }
    });
  }

  private newControlInGroup(name: string, value: string, type: SubbotParameterType, regexp: RegExp): FormGroup {
    const control = new FormControl(value ?? '', Validators.required);

    if (type === SubbotParameterType.integer) {
      control.addValidators(Validators.pattern(/^(\d)+$/));
    } else if (regexp) {
      control.addValidators(Validators.pattern(regexp));
    }

    const nestedFormGroup = this.formBuilder.group({});
    nestedFormGroup.addControl(name, control);

    return nestedFormGroup;
  }

  addFieldInListType(indexInParentFormArray: number, name: string) {
    const group = this.newControlInGroup(`${name}`, '', SubbotParameterType.list, null);
    this.getNestedFormArray(indexInParentFormArray).push(group);
  }

  removeFieldInListType(indexInParentFormArray: number, indexInNestedFormArray: number) {
    this.getNestedFormArray(indexInParentFormArray).removeAt(indexInNestedFormArray);
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
